import { GetterTree } from 'vuex'
import { ScheduleStateInterface } from 'src/store/schedule/state'
import { StoreInterface } from 'src/store'
import { OptionsValue, Schedule, ScheduleGroupByDate } from 'src/types'
import { Item, LessonPlan, LevelId, Section, SubjectId, UserGroup } from 'able-lib'
import { DateTimeFormatOptions } from 'src/lib/types'

const getters: GetterTree<ScheduleStateInterface, StoreInterface> = {
  getSchedulesGroupByDate: (state: ScheduleStateInterface): Array<ScheduleGroupByDate> => {
    const lessons: Array<Item> = state.schedules
    const schedules: Array<Schedule> = []

    lessons.forEach((lesson: Item) => {
      const shortTimeFormat: DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: true }
      const startDateTime = new Date(lesson?.startDateTime)
      const endDateTime = new Date(lesson?.endDateTime)
      const startDateTimeFormatted = startDateTime?.toLocaleTimeString('en-US', shortTimeFormat)
      const endDateTimeFormatted = endDateTime?.toLocaleTimeString('en-US', shortTimeFormat)

      const lessonPlan: LessonPlan = lesson?.lessonPlan ? lesson.lessonPlan : lesson.originalItem.lessonPlan

      schedules.push({
        id: lesson.id,
        date: startDateTime.toDateString(),
        startDateTimestamp: startDateTime.getTime(),
        endDateTimestamp: endDateTime.getTime(),
        time: `${startDateTimeFormatted} - ${endDateTimeFormatted}`,
        label: lessonPlan ? lessonPlan?.level?.name + ' - ' + lessonPlan?.subject?.name : 'N/A'
      })
    })

    const dates = [...new Set(schedules.map((schedule: Schedule) => schedule.date))]

    dates.sort((itemA: string, itemB: string) => {
      const dateAInNumber = Number(new Date(itemA))
      const dateBInNumber = Number(new Date(itemB))
      // Order in descending
      return dateAInNumber - dateBInNumber
    })

    const groupOfSchedules = []

    dates.forEach((date: string) => {
      const schedulesByDate = schedules.filter((schedule: Schedule) => schedule.date === date)
        .sort((a, b) => a.startDateTimestamp - b.startDateTimestamp)
      groupOfSchedules.push({
        date,
        schedules: schedulesByDate
      })
    })

    return groupOfSchedules
  },

  getSectionsOptions: (state: ScheduleStateInterface) => (levelId: LevelId): Array<OptionsValue> => {
    const sections = state.sections.filter((section: Section) => section.level.id === levelId)

    const filteredSections = sections.filter((v, i, a) => a.findIndex(t => (t.group.id === v.group.id)) === i)

    return filteredSections.map((section: Section) => {
      return {
        label: section.group.name,
        value: section.group.id
      }
    })
  },

  getTemplateLessonPlansOptions: (state: ScheduleStateInterface) => (levelId?: LevelId, subjectId?: SubjectId): Array<Item> => {
    const filteredItems = state.originalItems.filter((item: Item) => {
      let willInclude = false
      if (levelId !== null && subjectId !== null) {
        if (item.lessonPlan.level.id === levelId && item.lessonPlan.subject.id === subjectId) {
          willInclude = true
        }
      } else if (levelId !== null) {
        if (item.lessonPlan.level.id === levelId) {
          willInclude = true
        }
      } else if (subjectId !== null) {
        if (item.lessonPlan.subject.id === subjectId) {
          willInclude = true
        }
      }

      return willInclude
    })

    const sortByLessonAsc = filteredItems.sort((itemA: Item, itemB: Item) => {
      if (itemA.lessonPlan.lesson < itemB.lessonPlan.lesson) return -1
      else if (itemA.lessonPlan.lesson > itemB.lessonPlan.lesson) return 1
      else return 0
    })

    return sortByLessonAsc.sort((itemA: Item, itemB: Item) => Number.parseFloat(itemA.lessonPlan.number) - Number.parseFloat(itemB.lessonPlan.number))
  },

  getScheduleValues: (state: ScheduleStateInterface): any => state.schedule,

  getUserPickerValues: (state: ScheduleStateInterface): any => state.userPicker,

  getUsersOptions: (state: ScheduleStateInterface) => (levelId = 0, sectionId = 0): Array<UserGroup> => {
    const foundSection = state.sections.find((section: Section) => section.level.id === levelId && section.group.id === sectionId)
    return foundSection?.group?.userGroups || []
  },

  getUsers: (state: ScheduleStateInterface): Array<UserGroup> => state.users
}

export default getters
